import { FILE_PREVIEW_LIMITS } from "../constants/filePreview";

export const PREVIEWABLE_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "image/bmp",
] as const;

export const PREVIEWABLE_PDF_TYPES = ["application/pdf"] as const;

export const PREVIEWABLE_TEXT_TYPES = [
  "text/plain",
  "text/csv",
  "text/html",
  "text/xml",
  "application/json",
  "application/xml",
] as const;

export type PreviewType = "image" | "pdf" | "text" | null;

export interface PreviewEligibility {
  canPreview: boolean;
  previewType: PreviewType;
  reason?: string;
}

/**
 * Determines if a file can be previewed based on its MIME type and size
 */
export function isPreviewable(
  mimeType: string | undefined,
  fileSize: number
): PreviewEligibility {
  if (!mimeType) {
    return {
      canPreview: false,
      previewType: null,
      reason: "Unknown file type",
    };
  }

  const mime = mimeType.toLowerCase();

  // Check images
  if (PREVIEWABLE_IMAGE_TYPES.includes(mime as any)) {
    if (fileSize > FILE_PREVIEW_LIMITS.image) {
      return {
        canPreview: false,
        previewType: "image",
        reason: "File exceeds 10 MB image preview limit",
      };
    }
    return { canPreview: true, previewType: "image" };
  }

  // Check PDFs
  if (PREVIEWABLE_PDF_TYPES.includes(mime as any)) {
    if (fileSize > FILE_PREVIEW_LIMITS.pdf) {
      return {
        canPreview: false,
        previewType: "pdf",
        reason: "File exceeds 25 MB PDF preview limit",
      };
    }
    return { canPreview: true, previewType: "pdf" };
  }

  // Check text files
  if (
    PREVIEWABLE_TEXT_TYPES.includes(mime as any) ||
    mime.startsWith("text/")
  ) {
    if (fileSize > FILE_PREVIEW_LIMITS.text) {
      return {
        canPreview: false,
        previewType: "text",
        reason: "File exceeds 5 MB text preview limit",
      };
    }
    return { canPreview: true, previewType: "text" };
  }

  return {
    canPreview: false,
    previewType: null,
    reason: "File type not supported for preview",
  };
}

/**
 * Gets the preview type for a given MIME type without size checking
 */
export function getPreviewType(mimeType: string | undefined): PreviewType {
  if (!mimeType) return null;

  const mime = mimeType.toLowerCase();

  if (PREVIEWABLE_IMAGE_TYPES.includes(mime as any)) return "image";
  if (PREVIEWABLE_PDF_TYPES.includes(mime as any)) return "pdf";
  if (PREVIEWABLE_TEXT_TYPES.includes(mime as any) || mime.startsWith("text/"))
    return "text";

  return null;
}

/**
 * Formats file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}
